// Ligne « date de livraison » du cart drawer (snippets/cart-delivery-date.liquid). La date est posée sur le
// produit marqueur gratuit livraison-a-une-date-precise, comme sur /cart?view=recap
// (cart-scheduled-delivery.js) : ici on rafraîchit les sections du tiroir au lieu de recharger la page.
// Le contenu est construit en JS et ignoré par le morph de section, qui ne met à jour que les attributs
// data-* (état du panier) : d'où attributeChangedCallback.
(function () {
  'use strict';
  if (customElements.get('sl-delivery-date')) return;

  const PRODUCT_HANDLE = 'livraison-a-une-date-precise';
  const DATE_COUNT = 60;
  // Repli si la ligne Chronopost Express de France est introuvable dans settings.delivery_info_modal_countries
  // (même repli que cart-summary.liquid).
  const CHRONOPOST_FALLBACK = { price: '9,90 €', freeFrom: 95 };

  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const route = (path) => shopRoot.replace(/\/?$/, '/') + path;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
  const track = (name, params) =>
    window.SendloveCartAnalytics?.track(name, Object.assign({ location: 'drawer' }, params));

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
  }

  // Une coupure réseau (TypeError de fetch) sur mobile est souvent passagère : un second essai. Réservé
  // aux appels idempotents — jamais cart/add.js, qui doublerait la ligne.
  async function withNetworkRetry(call) {
    try {
      return await call();
    } catch (error) {
      if (!(error instanceof TypeError)) throw error;
      await wait(800);
      return call();
    }
  }

  async function getCart() {
    return withNetworkRetry(async () => {
      const response = await fetch(route('cart.js'), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Shopify Cart API: ' + response.status);
      return response.json();
    });
  }

  async function post(path, body, retry) {
    const send = async () => {
      const response = await fetch(route(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('Shopify Cart API: ' + response.status);
      const data = await response.json();
      if (data.errors) throw new Error(typeof data.errors === 'string' ? data.errors : 'Shopify Cart API error');
      return data;
    };
    return retry ? withNetworkRetry(send) : send();
  }

  function markerItems(cart) {
    return (cart.items || []).filter((item) => item.handle === PRODUCT_HANDLE);
  }

  function cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId).join(',');
  }

  class SlDeliveryDate extends HTMLElement {
    static get observedAttributes() {
      return ['data-scheduled', 'data-scheduled-date', 'data-cart-total'];
    }

    connectedCallback() {
      this.row = this.querySelector('[data-cdd-row]');
      this.select = this.querySelector('[data-cdd-select]');
      this.text = this.querySelector('[data-cdd-text]');
      this.action = this.querySelector('[data-cdd-action]');
      this.clear = this.querySelector('[data-cdd-clear]');
      this.statusEl = this.querySelector('[data-cdd-status]');
      if (!this.row || !this.select || !this.text || !this.action || !this.clear || !this.statusEl) return;

      if (!this.bound) {
        this.bound = true;
        this.select.addEventListener('change', () => this.save(this.select.value));
        this.clear.addEventListener('click', () => this.save(''));
      }
      // « Livré dès » dépend du cutoff de 11h30 et la liste du jour : à jour si le tiroir reste ouvert.
      // Seulement si l'une des deux a changé : reconstruire la liste fermerait un sélecteur ouvert.
      if (!this.timer) this.timer = setInterval(() => {
        if (this.calendarKey() !== this.renderedKey) this.render();
      }, 60000);
      this.render();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.timer = null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== newValue && this.select) this.render();
    }

    get isScheduled() {
      return this.dataset.scheduled === 'true';
    }

    get scheduledDate() {
      return this.dataset.scheduledDate || '';
    }

    chronopostRate() {
      if (this.rate) return this.rate;
      this.rate = CHRONOPOST_FALLBACK;
      try {
        const countries = JSON.parse(this.querySelector('[data-cdd-countries]')?.textContent || '[]');
        const france = Array.isArray(countries) && countries.find((country) => String(country?.name || '').trim().toLowerCase() === 'france');
        const line = String(france?.description || '').split('\n').find((entry) => /\*\*\s*chronopost express\s*\*\*/i.test(entry));
        if (line) {
          const parts = line.split(/\s+-\s+/).map((part) => part.trim());
          const price = parts.find((part) => /€/.test(part) && !/gratuit/i.test(part));
          const free = /gratuit[^\d]*(\d+(?:[.,]\d+)?)/i.exec(line);
          this.rate = {
            price: price ? price.replace(/\s*€/, ' €') : CHRONOPOST_FALLBACK.price,
            freeFrom: free ? parseFloat(free[1].replace(',', '.')) : null,
          };
        }
      } catch (error) { /* Réglage illisible : repli. */ }
      return this.rate;
    }

    calendarKey() {
      const calendar = window.SendloveDeliveryCalendar;
      return calendar ? calendar.minDate() + '|' + calendar.earliestDelivery() : '';
    }

    // « 9,90 € », ou « offert » au-delà du seuil de gratuité Chronopost.
    chronopostPrice() {
      const rate = this.chronopostRate();
      const total = (parseInt(this.dataset.cartTotal, 10) || 0) / 100;
      return rate.freeFrom !== null && total >= rate.freeFrom ? 'offert' : rate.price;
    }

    render() {
      const calendar = window.SendloveDeliveryCalendar;
      if (!calendar) return;
      this.renderedKey = this.calendarKey();
      const date = this.scheduledDate;
      const dateIsValid = this.isScheduled && calendar.dateReason(date) === null;

      const price = this.chronopostPrice();
      const dates = calendar.dates(DATE_COUNT);
      // Une date enregistrée peut dépasser l'horizon de la liste : on l'y ajoute si elle reste livrable.
      if (dateIsValid && !dates.includes(date)) dates.push(date);
      // Le coût est rappelé dans la liste elle-même (titre de groupe, visible dans la roue iOS) :
      // la planification ne doit jamais être une surprise au checkout.
      const group = document.createElement('optgroup');
      group.label = 'Date précise · Chronopost ' + price;
      group.append(...dates.map((value) => new Option(capitalize(calendar.longLabel(value)), value)));
      // Dates chaudes fermées (Noël…) : une ligne grisée à la place du trou dans la liste.
      calendar.closedPeaks(dates).forEach((notice) => {
        const option = new Option(notice.label, 'closed');
        option.disabled = true;
        const next = Array.from(group.children).find((item) => item.value === notice.before);
        group.insertBefore(option, next || null);
      });
      this.select.replaceChildren(new Option('Dès que possible', ''), group);
      this.select.value = dateIsValid ? date : '';

      this.row.classList.toggle('is-scheduled', this.isScheduled);
      this.clear.hidden = !this.isScheduled;
      // Le morph pose les attributs un par un (data-scheduled avant data-scheduled-date) : un rendu
      // intermédiaire peut voir une date vide. Son message est retiré au rendu suivant, sans effacer
      // une erreur d'enregistrement.
      if (this.staleDateStatus && !(this.isScheduled && !dateIsValid)) {
        this.staleDateStatus = false;
        this.showStatus('', false);
      }
      if (dateIsValid) {
        this.text.innerHTML = 'Livré le <strong>' + escapeHtml(calendar.shortLabel(date)) + '</strong> · Chronopost ' + escapeHtml(price);
        this.action.textContent = 'Modifier';
      } else if (this.isScheduled) {
        this.text.innerHTML = 'Date de livraison à choisir' +
          '<span class="sl-cdd__sub">Chronopost · ' + escapeHtml(price) + '</span>';
        this.action.textContent = 'Choisir';
        this.showStatus('La date choisie n’est plus disponible. Choisissez-en une autre.', true);
        this.staleDateStatus = true;
      } else {
        // Le coût de la date précise est visible avant tout tap, pas seulement une fois la date choisie.
        this.text.innerHTML = 'Livré dès <strong>' + escapeHtml(calendar.shortLabel(calendar.earliestDelivery())) + '</strong>' +
          '<span class="sl-cdd__sub">ou à date précise en Chronopost · ' + escapeHtml(price) + '</span>';
        this.action.textContent = 'Planifier';
      }
    }

    showStatus(message, isError) {
      this.staleDateStatus = false;
      this.statusEl.textContent = message || '';
      this.statusEl.hidden = !message;
      this.statusEl.classList.toggle('is-error', !!isError);
    }

    // Le loader du bouton « Valider mon cadeau » (cart-drawer-footer) suit .cart-items-disabled.
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', value ? 'true' : 'false');
      this.row.classList.toggle('is-busy', value);
      this.select.disabled = value;
      this.clear.disabled = value;
      this.closest('cart-items-component')?.classList.toggle('cart-items-disabled', value);
    }

    variantId() {
      const id = Number(this.dataset.variantId);
      if (!id) throw new Error('Le produit « Livraison à une date précise » est indisponible.');
      if (Number(this.dataset.variantPrice) !== 0) throw new Error('Le produit de planification doit être gratuit.');
      return id;
    }

    // value : date AAAA-MM-JJ à planifier, ou '' pour revenir à « Dès que possible ».
    async save(value) {
      if (this.busy) return;
      const calendar = window.SendloveDeliveryCalendar;
      const choice = value ? 'scheduled' : 'soon';
      if (value && calendar.dateReason(value) !== null) {
        track('cart_delivery_save_result', { outcome: 'invalid_date', reason: calendar.dateReason(value), delivery_date: value });
        this.render();
        this.showStatus('Cette date n’est plus disponible. Choisissez-en une autre.', true);
        return;
      }

      this.setBusy(true);
      this.showStatus('', false);
      track('cart_delivery_save_attempt', { delivery_choice: choice });
      const startedAt = Date.now();
      try {
        const cart = await getCart();
        const items = markerItems(cart);
        const sections = cartSectionIds();
        let response = null;

        // Un seul marqueur suffit à la Function de checkout.
        for (const item of value ? items.slice(1) : items) {
          response = await post('cart/change.js', { id: item.key, quantity: 0, sections }, true);
        }
        if (value) {
          const properties = { 'Date de livraison': calendar.cartLabel(value), '_date_livraison_iso': value };
          const item = items[0];
          if (!item) {
            response = await post('cart/add.js', { id: this.variantId(), quantity: 1, properties, sections }, false);
          } else if (item.quantity !== 1 || item.properties?.['_date_livraison_iso'] !== value ||
              item.properties?.['Date de livraison'] !== properties['Date de livraison']) {
            response = await post('cart/change.js', {
              id: item.key,
              quantity: 1,
              properties: Object.assign({}, item.properties || {}, properties),
              sections,
            }, true);
          }
        }

        track('cart_delivery_save_result', { outcome: 'saved', delivery_choice: choice, duration_ms: Date.now() - startedAt });
        if (response) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: {}, sourceId: 'scheduled-delivery', data: { source: 'scheduled-delivery', sections: response.sections || {} } },
          }));
        }
      } catch (error) {
        track('cart_delivery_save_result', {
          outcome: 'request_error',
          delivery_choice: choice,
          error_name: String(error?.name || 'Error').replace(/[^A-Za-z]/g, '').slice(0, 40) || 'Error',
          duration_ms: Date.now() - startedAt,
        });
        console.error('[SlDeliveryDate] Save failed:', error);
        this.showStatus(value ? 'Impossible d’enregistrer la date. Réessayez.' : 'Impossible de retirer la date. Réessayez.', true);
      } finally {
        this.setBusy(false);
        // Sans changement ou après une erreur, la liste revient à l'état réel du panier.
        this.render();
      }
    }

    // Appelé avant le départ au checkout : une date enregistrée hier peut ne plus être planifiable.
    blocksCheckout() {
      if (this.busy) {
        track('cart_checkout_blocked', { reason: 'delivery_busy' });
        return true;
      }
      if (!this.isScheduled || window.SendloveDeliveryCalendar?.dateReason(this.scheduledDate) === null) return false;
      track('cart_checkout_blocked', { reason: 'invalid_delivery_date' });
      this.render();
      this.select.focus();
      return true;
    }
  }

  customElements.define('sl-delivery-date', SlDeliveryDate);

  // Phase de capture : passe avant la proposition double peluche du tiroir (cart-drawer.liquid), qui
  // écoute le même clic en phase de bulle et part vers /checkout.
  document.addEventListener('click', (event) => {
    const checkout = event.target.closest('#checkout');
    if (!checkout) return;
    const row = checkout.closest('cart-items-component')?.querySelector('sl-delivery-date');
    if (!row || typeof row.blocksCheckout !== 'function' || !row.blocksCheckout()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
})();
